import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import StyleProvider from 'aquilla/styled/StyleProvider';
import NewFooter from '@layout/Footer/NewFooter';
import useTTUStore from '@stores/ttu';
import { getShowTTUCurtain, getTTUShow } from '@stores/selectors/ttuSelectors';
import Dynamic from '@base/Dynamic/Dynamic';
import { noOp } from '@constants/empty';
import { useIsMobile } from '@stores/AppCommonData';
import { showTTUOption } from '@api/fetchers/ttu';
import TTUWidgetRenderer from '@layout/TTUWidgetRenderer';
import AiDoubtsPill from '@cont/DoubtsUnacademyAI/AiDoubtsPill';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { Spacings } from '@styles/index';
import Header from '../Header/NewGoalHeader';

const TTURenderer = Dynamic(() => import('@layout/TTURenderer'));

const Container = styled.div`
  --layout-width: 1136px;
  --layout-tab-width: 768px;
  --layout-mweb-width: 328px;
  background-color: ${({ $whiteBackground }) =>
    $whiteBackground ? 'var(--color-base-1)' : 'var(--color-base-0)'};
`;

const baseConfig = {
  mode: 'light'
};

const NewGoalLayout = (props) => {
  const { children, layoutOptions = {}, pageMetaInfo } = props;
  const {
    ttuOptions = {},
    hideIntercom = false,
    hideFooter = false,
    whiteBackground = false
  } = layoutOptions;

  const { query, pathname } = useRouter();
  const currentGoalUID = query.goalUID;
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const insideApp = query.inside_app === 'true' || false;
  const isMobile = useIsMobile();

  const [backButton, setBackButton] = useState({
    value: false,
    fn: noOp
  });
  const handleBackPress = () => backButton.fn();

  const ttuShow = useTTUStore(getTTUShow);
  const showTTUCurtain = useTTUStore(getShowTTUCurtain);
  const [showTTU, setShowTTU] = useState(ttuShow);
  const [showTTUTooltip, setShowTTUTooltip] = useState(
    ttuOptions.openByDefault
  );
  const [ttuTriggerSource, setTTUTriggerSource] = useState('Scroll Trigger');

  const { goalInfo } = useGoalInfo(currentGoalUID);
  const { isAiDoubtsEnabled, uid: goalUID, slug: goalSlug } = goalInfo || {};

  const isStorePage = useMemo(
    () => pathname === '/store' || pathname.includes('/store'),
    [pathname]
  );

  const onCloseTTU = () => {
    setBackButton({ value: false, fn: noOp });
    setShowTTU(false);
    showTTUCurtain(false);
    showTTUOption(currentGoalUID);
  };

  useEffect(() => {
    setShowTTU(ttuShow);
  }, [ttuShow]);

  return (
    <>
      <StyleProvider config={baseConfig}>
        <Header pageMetaInfo={pageMetaInfo} />
        <Container $whiteBackground={whiteBackground}>{children}</Container>
        {!insideApp && !hideFooter && <NewFooter />}
        {!isMobile && (
          <TTURenderer
            onCloseTTU={onCloseTTU}
            showTTU={showTTU}
            backButton={backButton}
            setBackButton={setBackButton}
            handleBackPress={handleBackPress}
            ttuTriggerSource={ttuTriggerSource}
            ttuOptions={ttuOptions}
          />
        )}
        {!isMobile && (
          <TTUWidgetRenderer
            showTTU={showTTU}
            setShowTTU={setShowTTU}
            showTTUTooltip={showTTUTooltip}
            setShowTTUTooltip={setShowTTUTooltip}
            ttuTriggerSource={ttuTriggerSource}
            setTTUTriggerSource={setTTUTriggerSource}
            hideIntercom={hideIntercom}
            ttuOptions={ttuOptions}
          />
        )}

        {(isAiDoubtsEnabled || !isLoggedIn) && !isStorePage ? (
          <div
            style={{
              position: 'fixed',
              bottom: isMobile ? Spacings.SPACING_26B : Spacings.SPACING_45B,
              right: Spacings.SPACING_6B
            }}
          >
            <AiDoubtsPill goalUID={goalUID} goalSlug={goalSlug} />
          </div>
        ) : null}
      </StyleProvider>
    </>
  );
};

export default NewGoalLayout;
